import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { MediaRenderer } from './ImagePlaceholder';
import { ArrowRightIcon } from './ArrowRightIcon';
import LikeButton from '@/components/LikeButton';
import type { EventCardProps } from '@/features/artists/types/types';

/**
 * Event card component for artist events
 */
export const EventCard: React.FC<EventCardProps> = ({ eventData, initialIsLiked = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const navigate = useNavigate();
  const event = eventData.event;

  // Sync isLiked state when initialIsLiked prop changes (after batched API call completes)
  useEffect(() => {
    setIsLiked(initialIsLiked);
  }, [initialIsLiked]);

  const handleCardClick = () => {
    if (event.business?.handle) {
      navigate(`/venues/${event.business.handle}/events/${event.id}`);
    }
  };

  const handleLikeToggle = (newLikedState: boolean) => {
    setIsLiked(newLikedState);
  };

  // Check if video is available and valid
  const hasValidVideo = event.card_video_id && 
    event.card_video_id !== '00000000-0000-0000-0000-000000000000' && 
    event.card_video_id.trim() !== '';

  const mediaVariants = {
    initial: { opacity: 0, scale: 1.05 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 1.05 },
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
      className="group relative bg-gray-900/50 border border-gray-800 rounded-3xl overflow-hidden transition-all duration-300 hover:border-blue-500/50 cursor-pointer"
    >
      <div className="relative">
        <div className="absolute inset-0 z-0">
          <AnimatePresence initial={false}>
            {!isHovered || !hasValidVideo ? (
              <motion.div key="image" {...mediaVariants} className="h-64 w-full">
                <MediaRenderer 
                  mediaId={event.card_image_id} 
                  alt={event.name}
                  className="!rounded-none h-full w-full object-cover" 
                />
              </motion.div>
            ) : (
              <motion.div key="video" {...mediaVariants} className="h-64 w-full">
                <MediaRenderer 
                  mediaId={event.card_video_id} 
                  alt={`${event.name} video`}
                  className="!rounded-none h-full w-full object-cover"
                  isVideo={true}
                  autoPlay={true}
                />
              </motion.div>
            )}
          </AnimatePresence>
          <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors duration-300"></div>
        </div>

        {/* Like Button - Top Right */}
        <div className="absolute top-4 right-4 z-20">
          <LikeButton
            entityId={event.id}
            entityType="events"
            isLiked={isLiked}
            onToggle={handleLikeToggle}
            size="md"
          />
        </div>

        <div className="relative z-10 p-6 h-64 flex flex-col justify-between">
          <div>
            <h3 className="text-xl font-serif font-medium text-white mb-2 line-clamp-2">
              {event.name}
            </h3>
            <p className="text-gray-300 text-sm line-clamp-2">
              {event.description}
            </p>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              <p className="font-medium">{event.business?.name || 'Unknown Venue'}</p>
              <p>{event.business?.location?.city || 'Unknown'}, {event.business?.location?.country || 'Unknown'}</p>
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick();
              }}
              className="group flex items-center justify-center w-10 h-10 bg-blue-600/80 text-white rounded-full transition-all duration-300 hover:bg-blue-500 hover:scale-110"
            >
              <ArrowRightIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
